import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  GSSidePanelPage,
  GSActionBar,
  GSButton,
  GSDivider,
  GSEmptyList,
  GSInfoGroup,
  GSInput,
  GSItemList,
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
} from "golfstatus_react_components";
import {
  faArrowLeft,
  faArrowRight,
  faChevronLeft,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useInlineResponseEditing } from "../../../../hooks/customFieldResponseHooks";
import {
  getCustomFieldResponsesSummary,
  clearCurrentResponsesSummary,
  clearNotifications,
  selectCurrentResponsesSummary,
  selectCustomFieldResponsesLoading,
} from "../../../../reducers/customFieldResponsesSlice";
import {
  getTournamentForm,
  getTournamentPackageFormFields,
  selectCurrentTournamentPackageForm,
  selectCurrentTournamentPackageFormFileds,
} from "../../../../reducers/packageSlice";
import TournamentOrderFormResponseAnswerItem from "../../tournament-reporting/tournament-order-form-response-answer-item";
import {
  slideoutContentStyle,
  slideoutPageStyle,
  slideoutRootStyle,
} from "../../../../templates/slideout-styles";

const NO_RESPONSE = "No response";

const getOptionLabel = (value) =>
  value === NO_RESPONSE ? "No Response" : value;

const responseCountLabel = (count) =>
  `${count} ${count === 1 ? "Response" : "Responses"}`;

const sectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad.apply("horizontal"),
  ...defaultPadding.xLargePad.apply("vertical"),
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
};

const filterSectionStyle = {
  ...sectionStyle,
  ...defaultPadding.smallLayoutPad.apply("vertical"),
};

const searchSectionStyle = {
  ...sectionStyle,
  ...defaultPadding.xLargePad.apply("vertical"),
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: golfstatusColors.white,
};

const bodySectionStyle = {
  ...sectionStyle,
  ...defaultGapping.mediumGap,
  ...defaultPadding.smallLayoutPad.apply("vertical"),
  borderBottom: "none",
};

const emptyBodyStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
};

const emptySectionStyle = {
  ...defaultPadding.largeLayoutPad.apply("vertical"),
  ...defaultPadding.xLargePad.apply("horizontal"),
};

const subtitleStyle = {
  ...defaultTypography.bodyRegular,
  color: golfstatusColors.grey800,
  opacity: 0.7,
};

const headerStackStyle = {
  ...defaultLayouts.vStack,
};

const headerRowStyle = {
  ...defaultLayouts.hStack.align("flex-start", "center"),
  ...defaultGapping.mediumGap,
};

const dividerStyle = {
  width: "1px",
  height: defaultTypography.bodyRegular.fontSize,
  backgroundColor: golfstatusColors.grey200,
};

const filterButtonStyle = (selected) => ({
  ...defaultLayouts.hStack.align("space-between", "center"),
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad,
  ...defaultBorders.mediumLargeBorderRadius,
  background: selected ? golfstatusColors.grey100 : golfstatusColors.white,
  border: `1px solid ${selected ? golfstatusColors.grey100 : golfstatusColors.grey200}`,
  cursor: selected ? "default" : "pointer",
});

const TournamentPackageCustomFieldResponses = () => {
  const match = useMatch(
    "/:tournamentID/promotion/packages/:packageID/forms/:formID/responses/:customFieldID"
  );
  const tournamentID = match?.params?.tournamentID;
  const packageID = match?.params?.packageID;
  const formID = match?.params?.formID;
  const customFieldID = match?.params?.customFieldID;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchSummary = useCallback(() => {
    if (tournamentID && customFieldID) {
      dispatch(
        getCustomFieldResponsesSummary({
          tournamentKey: tournamentID,
          questionKey: customFieldID,
        })
      );
    }
  }, [dispatch, tournamentID, customFieldID]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(
    () => () => {
      dispatch(clearCurrentResponsesSummary());
      dispatch(clearNotifications());
    },
    [dispatch]
  );

  useEffect(() => {
    if (formID) {
      dispatch(getTournamentForm(formID));
      dispatch(getTournamentPackageFormFields(formID));
    }
  }, [dispatch, formID]);

  const summary = useSelector(selectCurrentResponsesSummary);
  const form = useSelector(selectCurrentTournamentPackageForm);
  const formFields = useSelector(selectCurrentTournamentPackageFormFileds);
  const loadingList = useSelector(selectCustomFieldResponsesLoading);
  const loadingSummary = loadingList?.includes?.("getCustomFieldResponsesSummary");

  const [search, setSearch] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(null);
  const {
    banner,
    editingKey,
    requestEdit,
    commitRef,
    setPendingEdit,
    leftNavigation,
  } = useInlineResponseEditing(() => navigate(-1));

  useEffect(() => {
    setSearch("");
    setSelectedFilter(null);
  }, [customFieldID]);

  const question = (summary ?? []).find(
    (entry) => `${entry?.id}` === `${customFieldID}`
  );
  const options = question?.options ?? [];
  const breakdown = question?.breakdown ?? [];
  const totalResponses = question?.totalResponses ?? 0;

  const responsesLoaded = Array.isArray(question?.responses);
  const allAnswers = question?.responses ?? [];

  const isNoResponseOption = (optionBreakdown) =>
    `${optionBreakdown?.value}` === NO_RESPONSE;

  let noResponseCount = breakdown.find(isNoResponseOption)?.count ?? 0;
  if (responsesLoaded) {
    noResponseCount = allAnswers.filter(
      (answer) => `${answer?.response?.value ?? ""}`.trim() === ""
    ).length;
  }

  const noResponseOption = { value: NO_RESPONSE, count: noResponseCount };
  let filterOptionBreakdowns = [...breakdown, noResponseOption];
  if (breakdown.some(isNoResponseOption)) {
    filterOptionBreakdowns = breakdown.map((optionBreakdown) =>
      isNoResponseOption(optionBreakdown) ? noResponseOption : optionBreakdown
    );
  }

  const orderedFields = [...(formFields ?? [])].sort(
    (a, b) => (a?.position ?? 0) - (b?.position ?? 0)
  );
  const currentIndex = orderedFields.findIndex(
    (field) => `${field?.id}` === `${customFieldID}`
  );

  const goToField = (field) => {
    if (field?.id) {
      navigate(
        `/${tournamentID}/promotion/packages/${packageID}/forms/${formID}/responses/${field.id}`
      );
    }
  };

  const getNavigation = () => ({
    title: "Form Details",
    leftIcon: faChevronLeft,
    leftButtonClick: leftNavigation,
  });

  const getHeaderActions = () => {
    if (orderedFields.length <= 1) {
      return [];
    }
    return [
      {
        actionIcon: faArrowLeft,
        type: "light-grey",
        isDisabled: currentIndex <= 0,
        actionClick: () => goToField(orderedFields[currentIndex - 1]),
      },
      {
        actionIcon: faArrowRight,
        type: "light-grey",
        isDisabled: currentIndex < 0 || currentIndex >= orderedFields.length - 1,
        actionClick: () => goToField(orderedFields[currentIndex + 1]),
      },
    ];
  };

  const getHeader = () => (
    <div style={headerStackStyle}>
      <div style={{ ...defaultTypography.headline3 }}>
        {question?.name}
        {question?.required && " *"}
      </div>
      <div style={headerRowStyle}>
        <div style={subtitleStyle}>{responseCountLabel(totalResponses)}</div>
        {form?.name && (
          <>
            <GSDivider style={dividerStyle} />
            <div style={subtitleStyle}>{form.name}</div>
          </>
        )}
      </div>
    </div>
  );

  const getHeaderSection = () => (
    <div style={sectionStyle}>
      <GSActionBar header={getHeader()} pageActions={getHeaderActions()} />
    </div>
  );

  const getFilterButton = (optionBreakdown) => {
    const selected = `${selectedFilter}` === `${optionBreakdown?.value}`;
    const noResponse = isNoResponseOption(optionBreakdown);
    const optionLabel = getOptionLabel(optionBreakdown?.value);
    const label = noResponse
      ? `${optionLabel} (${optionBreakdown?.count ?? 0})`
      : optionLabel;
    let primaryStyle = {
      ...defaultTypography.body,
      whiteSpace: "normal",
    };
    if (noResponse) {
      primaryStyle = {
        ...primaryStyle,
        ...defaultPadding.mediumPad.apply("vertical"),
      };
    }
    const sectionItems = [
      {
        type: "primary full-width",
        value: label,
        sectionItemStyle: primaryStyle,
      },
    ];
    if (!noResponse) {
      sectionItems.push({
        type: "full-width",
        value: responseCountLabel(optionBreakdown?.count ?? 0),
        sectionItemStyle: subtitleStyle,
      });
    }
    const groups = [{ fit: "stretch", sections: [{ sectionItems }] }];
    return (
      <div
        key={optionBreakdown?.value}
        style={filterButtonStyle(selected)}
        onClick={selected ? undefined : () => setSelectedFilter(optionBreakdown?.value)}
      >
        <GSInfoGroup dataGroups={groups} />
        {selected && (
          <GSButton
            buttonIcon={faTimesCircle}
            onClick={(event) => {
              event?.stopPropagation?.();
              setSelectedFilter(null);
            }}
          />
        )}
      </div>
    );
  };

  const getFilterSection = () => {
    if (question?.fieldType !== "select" || breakdown.length === 0) {
      return null;
    }
    const optionBreakdowns = selectedFilter
      ? filterOptionBreakdowns.filter(
          (optionBreakdown) => `${optionBreakdown?.value}` === `${selectedFilter}`
        )
      : filterOptionBreakdowns;
    return (
      <div style={filterSectionStyle}>
        <GSItemList
          style={{ ...defaultLayouts.fullWidth }}
          type="vertical medium-gap"
          items={optionBreakdowns}
          listItem={(optionBreakdown) => getFilterButton(optionBreakdown)}
        />
      </div>
    );
  };

  const getSearchSection = () => (
    <div style={searchSectionStyle}>
      <GSInput
        textValue={search}
        placeholder="Search..."
        leftIcon={faSearch}
        onChange={(event) => setSearch(event.target.value)}
      />
    </div>
  );

  const matchesFilter = (answer) => {
    if (!selectedFilter) {
      return true;
    }
    const value = `${answer?.response?.value ?? ""}`;
    if (selectedFilter === NO_RESPONSE) {
      return value.trim() === "";
    }
    if (selectedFilter === "Other" && !options.includes("Other")) {
      return value.trim() !== "" && !options.includes(value);
    }
    return value === selectedFilter;
  };

  const matchesSearch = (answer) => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return true;
    }
    const name = `${answer?.record?.name ?? ""}`.toLowerCase();
    const value = `${answer?.response?.value ?? ""}`.toLowerCase();
    return name.includes(term) || value.includes(term);
  };

  const answers = [...allAnswers]
    .filter(matchesFilter)
    .filter(matchesSearch)
    .sort((a, b) =>
      `${a?.record?.name ?? ""}`.localeCompare(`${b?.record?.name ?? ""}`)
    );

  const selectedOptionBreakdown = filterOptionBreakdowns.find(
    (optionBreakdown) => `${optionBreakdown?.value}` === `${selectedFilter}`
  );
  const selectedFilterEmpty =
    selectedFilter != null && (selectedOptionBreakdown?.count ?? 0) === 0;
  const isFiltering = search.trim() !== "" || selectedFilter != null;

  const clearFilter = () => setSelectedFilter(null);
  const clearAll = () => {
    setSearch("");
    setSelectedFilter(null);
  };

  const getEmptyState = () => {
    if (selectedFilterEmpty) {
      return (
        <GSEmptyList
          style={emptySectionStyle}
          title={getOptionLabel(selectedFilter)}
          detail="No one has selected this answer yet."
          actions={[{ title: "Clear Filter", type: "grey", onClick: clearFilter }]}
        />
      );
    }
    if (isFiltering) {
      return (
        <GSEmptyList
          style={emptySectionStyle}
          title="We couldn't find the response you're looking for..."
          detail="Try changing the search term and filter."
          actions={[{ title: "Clear All", type: "grey", onClick: clearAll }]}
        />
      );
    }
    return (
      <GSEmptyList
        style={emptySectionStyle}
        title="No Responses Found"
        detail="There are no responses to this question yet."
      />
    );
  };

  const getBodySection = () => {
    if (answers.length === 0) {
      return <div style={emptyBodyStyle}>{getEmptyState()}</div>;
    }
    return (
      <div style={bodySectionStyle}>
        <GSItemList
          type="vertical medium-gap"
          style={{ ...defaultLayouts.fullWidth }}
          items={answers}
          listItem={(answer) => (
            <div
              key={`${question?.id}-${answer?.record?.id}`}
              style={{ ...defaultLayouts.fullWidth }}
            >
              <TournamentOrderFormResponseAnswerItem
                record={answer?.record}
                response={answer?.response}
                question={question}
                editingKey={editingKey}
                onEdit={requestEdit}
                commitRef={commitRef}
                onSaved={fetchSummary}
                onUnsaved={setPendingEdit}
              />
            </div>
          )}
        />
      </div>
    );
  };

  const getContent = () => (
    <div style={slideoutContentStyle}>
      {getHeaderSection()}
      {getFilterSection()}
      {getSearchSection()}
      {getBodySection()}
    </div>
  );

  return (
    <tournament-package-custom-field-responses style={slideoutRootStyle}>
      <GSSidePanelPage
        header={getNavigation()}
        loading={loadingSummary && !responsesLoaded}
        banner={banner}
        content={getContent()}
        style={slideoutPageStyle}
      ></GSSidePanelPage>
    </tournament-package-custom-field-responses>
  );
};

export default TournamentPackageCustomFieldResponses;
